import { useLocalSearchParams } from 'expo-router';
import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { ChipsStrip } from '@/components/fantasy/ChipsStrip';
import { TransferStrip } from '@/components/fantasy/TransferStrip';
import { DeadlineCountdown } from '@/components/fantasy/DeadlineCountdown';
import { FixtureCard } from '@/components/matches/FixtureCard';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  pickCurrentGameweek,
  useFantasySeasons,
  useGameweeks,
  useMyFantasyTeam,
  useSquadPicks,
} from '@/features/fantasy/hooks';
import { useProvisionalGwPoints } from '@/features/fantasy/provisional';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { Position } from '@scoring/rules.ts';

export default function GameweekScreen() {
  const { seasonId } = useLocalSearchParams<{ seasonId: string }>();
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();

  const seasonsQuery = useFantasySeasons();
  const season = (seasonsQuery.data ?? []).find((entry) => entry.id === seasonId);
  const gameweeksQuery = useGameweeks(seasonId);
  const currentGw = pickCurrentGameweek(gameweeksQuery.data);
  const myTeamQuery = useMyFantasyTeam(seasonId);
  const picksQuery = useSquadPicks(myTeamQuery.data?.id);

  const myTeamIds = useMemo(() => {
    const ids = new Set<string>();
    for (const pick of picksQuery.data ?? []) {
      const teamId = pick.player?.team?.id;
      if (teamId) ids.add(teamId);
    }
    return ids;
  }, [picksQuery.data]);

  const provisional = useProvisionalGwPoints({
    competitionId: season?.competition_id,
    season: season?.season,
    roundLabel: currentGw?.round_label,
    playerPositions: new Map<string, Position>(),
  });

  const relevantFixtures = useMemo(
    () =>
      myTeamIds.size === 0
        ? provisional.fixtures
        : provisional.fixtures.filter(
            (fixture) =>
              (fixture.home_team && myTeamIds.has(fixture.home_team.id)) ||
              (fixture.away_team && myTeamIds.has(fixture.away_team.id)),
          ),
    [provisional.fixtures, myTeamIds],
  );

  if (gameweeksQuery.isPending || seasonsQuery.isPending) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.title')} />
        <View className="gap-4 px-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </View>
      </Screen>
    );
  }

  if (gameweeksQuery.isError) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.title')} />
        <ErrorState error={gameweeksQuery.error} onRetry={() => gameweeksQuery.refetch()} />
      </Screen>
    );
  }

  if (!currentGw) {
    return (
      <Screen>
        <BackHeader title={t('fantasy.title')} />
        <EmptyState
          icon="calendar-outline"
          title={t('fantasy.gameweek.noGameweekTitle')}
          body={t('fantasy.gameweek.noGameweekBody')}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <BackHeader
        title={t('fantasy.gameweek.title', {
          number: localizeDigits(String(currentGw.number), arabicNumerals),
        })}
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
        <Card className="mx-4 mb-4 px-4 py-4">
          <DeadlineCountdown deadlineIso={currentGw.deadline_at} />
          <AppText variant="micro" className="mt-3 leading-5">
            {t('fantasy.gameweek.transfersNote')}
          </AppText>
        </Card>

        <TransferStrip fantasySeasonId={seasonId} />

        <ChipsStrip fantasyTeamId={myTeamQuery.data?.id} gameweek={currentGw} />

        <View className="px-4 pb-2">
          <AppText variant="heading">{t('fantasy.gameweek.yourFixtures')}</AppText>
        </View>

        {relevantFixtures.length === 0 ? (
          <EmptyState
            icon="calendar-outline"
            title={t('matches.noMatchesTitle')}
            body={t('matches.noMatchesBody')}
          />
        ) : (
          <Card className="mx-4 overflow-hidden">
            {relevantFixtures.map((fixture, index) => (
              <View key={fixture.id} className={index > 0 ? 'border-t border-line' : ''}>
                <FixtureCard fixture={fixture} />
              </View>
            ))}
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}
